/**
 * Read a project export bundle from a file the user picked (CLAUDE.md 13).
 *
 * The import endpoint takes the parsed bundle as its JSON body, so the browser is
 * where the file becomes an object -- and therefore where a file that is not a bundle
 * at all should be refused, before it costs an 8 MB upload to be told so.
 *
 * The refusals reuse the server's own message keys rather than inventing client-side
 * wording. `error.project.import_too_large` and `error.project.import_invalid` are
 * already translated in both languages, and a locally-worded twin would eventually
 * disagree with the server about the same refusal -- which reads to the user as two
 * different problems.
 *
 * The cap mirrors `MAX_BUNDLE_BYTES` in `backend/app/api/projects.py`.
 */
import type { ProjectBundle } from '@/api/types'

export const MAX_BUNDLE_MB = 8
export const MAX_BUNDLE_BYTES = MAX_BUNDLE_MB * 1024 * 1024

export type BundleReadResult =
  | { ok: true; bundle: ProjectBundle }
  | { ok: false; messageKey: string; params: Record<string, unknown> }

function refuse(messageKey: string, params: Record<string, unknown> = {}): BundleReadResult {
  return { ok: false, messageKey, params }
}

/**
 * Read a file as text.
 *
 * `FileReader` rather than `Blob.text()`: the latter is newer, and jsdom does not
 * implement it at all, which would leave the parse path -- the whole point of this
 * module -- untestable. `FileReader` has been in every browser for a decade and
 * behaves identically here.
 */
function readText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result ?? ''))
    reader.onerror = () => reject(reader.error ?? new Error('read failed'))
    reader.readAsText(file)
  })
}

/**
 * Parse and sanity-check a bundle file.
 *
 * The shape check goes no further than "this is an object with a format version and a
 * project", deliberately. The server owns the schema; a stricter client check would
 * eventually refuse a bundle the server would have accepted, and the user has no way
 * to tell which of the two was wrong.
 */
export async function readProjectBundle(file: File): Promise<BundleReadResult> {
  if (file.size > MAX_BUNDLE_BYTES) {
    return refuse('error.project.import_too_large', { max_mb: MAX_BUNDLE_MB })
  }

  let parsed: unknown
  try {
    parsed = JSON.parse(await readText(file))
  } catch {
    return refuse('error.project.import_invalid')
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
    return refuse('error.project.import_invalid')
  }

  const candidate = parsed as Partial<ProjectBundle>
  if (
    typeof candidate.format_version !== 'number' ||
    typeof candidate.project !== 'object' ||
    candidate.project === null
  ) {
    return refuse('error.project.import_invalid')
  }

  return { ok: true, bundle: parsed as ProjectBundle }
}
